import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { noop } from 'lodash-es';
import { ReactNode, useRef } from 'react';
import useDoubleClick from 'use-double-click';

export interface PageButtonsProps {
	primaryButtonLabel?: string;
	primaryButtonAction?: () => void;
	isPrimaryButtonSubmitType?: boolean;
	displayLoadingIndicator?: boolean;
	primaryButtonDisabled?: boolean;
	primaryButtonFormId?: string;
	primaryButtonProps?: ButtonProps;
	secondaryButtonLabel?: string;
	secondaryButtonAction?: () => void;
	secondaryButtonProps?: ButtonProps;
	secondaryButtonDisabled?: boolean;
	withDivider?: boolean;
	endIconButton?: ReactNode;
}

export const PageButtons = ({
	primaryButtonLabel,
	primaryButtonAction = noop,
	isPrimaryButtonSubmitType = false,
	displayLoadingIndicator = false,
	primaryButtonDisabled = false,
	primaryButtonFormId,
	primaryButtonProps,
	secondaryButtonLabel,
	secondaryButtonAction = noop,
	secondaryButtonProps,
	secondaryButtonDisabled = false,
	withDivider = true,
	endIconButton = undefined,
}: PageButtonsProps): React.JSX.Element => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	useDoubleClick({
		onSingleClick: primaryButtonAction,
		onDoubleClick: noop,
		ref: buttonRef,
		latency: 200,
	});

	const isMobile = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);

	const buttonSize = isMobile ? 'medium' : 'large';

	return (
		<>
			{withDivider && <Divider></Divider>}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					gap: 2,
					justifyContent: secondaryButtonLabel
						? 'flex-end'
						: 'center',
					pt: 4,
				}}>
				{secondaryButtonLabel && (
					<Button
						variant="outlined"
						color="secondary"
						size={buttonSize}
						onClick={secondaryButtonAction}
						endIcon={endIconButton}
						sx={{ mr: 'auto' }}
						disabled={secondaryButtonDisabled}
						{...secondaryButtonProps}>
						{secondaryButtonLabel}
					</Button>
				)}
				<Button
					disabled={displayLoadingIndicator || primaryButtonDisabled}
					variant="contained"
					color="secondary"
					{...primaryButtonProps}
					size={buttonSize}
					type={isPrimaryButtonSubmitType ? 'submit' : undefined}
					form={
						isPrimaryButtonSubmitType
							? primaryButtonFormId
							: undefined
					}
					endIcon={
						<>
							{!displayLoadingIndicator && endIconButton}
							{displayLoadingIndicator && (
								<CircularProgress size="1rem" color="inherit" />
							)}
						</>
					}
					ref={buttonRef}
					sx={{
						visibility: primaryButtonLabel ? 'visible' : 'hidden',
					}}>
					{primaryButtonLabel}
				</Button>
			</Box>
		</>
	);
};
